import React from 'react';
import {
  Alert, AlertProps, Autocomplete, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, Grid, IconButton,
  InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography, useTheme,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { IOCType } from '../../types/ticketDetails';
import {
  Nis2EarlyWarningData, Nis2EntityProfile, Nis2FinalData, Nis2IntermediateData, Nis2NotificationData,
  Nis2Significance,
} from '../../types/nis2';
import {
  SEVERITY_OPTIONS, THREAT_CATEGORY_OPTIONS, financialThreshold, fromLocalInput, indicatedCriteria,
  toLocalInput,
} from './nis2Helpers';

// ─── Shared primitives ───────────────────────────────────────────────────────

const EU_MEMBER_STATES: [string, string][] = [
  ['AT', 'Austria'], ['BE', 'Belgium'], ['BG', 'Bulgaria'], ['HR', 'Croatia'], ['CY', 'Cyprus'],
  ['CZ', 'Czechia'], ['DK', 'Denmark'], ['EE', 'Estonia'], ['FI', 'Finland'], ['FR', 'France'],
  ['DE', 'Germany'], ['GR', 'Greece'], ['HU', 'Hungary'], ['IE', 'Ireland'], ['IT', 'Italy'],
  ['LV', 'Latvia'], ['LT', 'Lithuania'], ['LU', 'Luxembourg'], ['MT', 'Malta'], ['NL', 'Netherlands'],
  ['PL', 'Poland'], ['PT', 'Portugal'], ['RO', 'Romania'], ['SK', 'Slovakia'], ['SI', 'Slovenia'],
  ['ES', 'Spain'], ['SE', 'Sweden'],
];
const MEMBER_STATE_NAMES = Object.fromEntries(EU_MEMBER_STATES);

const IOC_TYPES = [...Object.values(IOCType), 'other'];

export const useInputSx = () => {
  const theme = useTheme();
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.tertiary,
      fontSize: '0.83rem',
      '& fieldset': { borderColor: theme.palette.border?.main ?? 'rgba(255,255,255,0.12)' },
      '&:hover fieldset': { borderColor: theme.palette.border?.strong ?? 'rgba(255,255,255,0.25)' },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
    },
    '& .MuiInputLabel-root': { fontSize: '0.83rem' },
  };
};

/**
 * MUI Alert derives its text/background from the palette with colour
 * manipulators that don't handle this theme's colours, which leaves white text on
 * a light tint. Use explicit theme tokens instead so it reads in both modes.
 */
export const Banner: React.FC<AlertProps & { severity: 'success' | 'info' | 'warning' | 'error' }> = ({
  severity, sx, ...props
}) => {
  const theme = useTheme();
  const main = theme.palette[severity].main;
  return (
    <Alert
      severity={severity}
      variant="standard"
      {...props}
      sx={{
        color: theme.palette.text.primary,
        backgroundColor: `color-mix(in srgb, ${main} 12%, transparent)`,
        border: `1px solid color-mix(in srgb, ${main} 40%, transparent)`,
        '& .MuiAlert-icon': { color: main },
        '& .MuiAlert-message': { color: theme.palette.text.primary },
        '& .MuiAlert-action .MuiIconButton-root': { color: theme.palette.text.secondary },
        ...(sx as object),
      }}
    />
  );
};

export const SectionLabel: React.FC<{ children: React.ReactNode; hint?: string }> = ({ children, hint }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 1.5, mt: 0.5 }}>
      <Typography sx={{
        fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase',
        color: theme.palette.text.tertiary,
      }}>
        {children}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: '0.72rem', color: theme.palette.text.tertiary, mt: 0.25 }}>{hint}</Typography>
      )}
    </Box>
  );
};

const CheckRow: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }> = ({
  checked, onChange, label, hint,
}) => {
  const theme = useTheme();
  return (
    <FormControlLabel
      sx={{ alignItems: 'flex-start', ml: -0.75, mb: 0.25 }}
      control={<Checkbox size="small" checked={!!checked} onChange={e => onChange(e.target.checked)}
        sx={{ color: theme.palette.text.tertiary, pt: 0.4 }} />}
      label={
        <Box sx={{ pt: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>{label}</Typography>
          {hint && <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary }}>{hint}</Typography>}
        </Box>
      }
    />
  );
};

const Text: React.FC<{
  label: string; value: string; onChange: (v: string) => void; rows?: number; placeholder?: string;
  helperText?: string;
}> = ({ label, value, onChange, rows, placeholder, helperText }) => (
  <TextField fullWidth size="small" label={label} value={value ?? ''} placeholder={placeholder}
    helperText={helperText} multiline={!!rows} minRows={rows}
    onChange={e => onChange(e.target.value)} sx={useInputSx()} />
);

const NumberInput: React.FC<{
  label: string; value: number | null; onChange: (v: number | null) => void; helperText?: string;
}> = ({ label, value, onChange, helperText }) => (
  <TextField fullWidth size="small" type="number" label={label} value={value ?? ''} helperText={helperText}
    onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
    inputProps={{ min: 0 }} sx={useInputSx()} />
);

const DateTimeInput: React.FC<{
  label: string; value: string; onChange: (iso: string) => void; helperText?: string;
}> = ({ label, value, onChange, helperText }) => (
  <TextField fullWidth size="small" type="datetime-local" label={label} value={toLocalInput(value)}
    helperText={helperText} InputLabelProps={{ shrink: true }}
    onChange={e => onChange(fromLocalInput(e.target.value) || '')} sx={useInputSx()} />
);

const SelectInput: React.FC<{
  label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[];
}> = ({ label, value, onChange, options }) => (
  <FormControl fullWidth size="small" sx={useInputSx()}>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value || ''} onChange={e => onChange(e.target.value)} sx={{ fontSize: '0.83rem' }}>
      <MenuItem value=""><em>Not specified</em></MenuItem>
      {options.map(o => <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.83rem' }}>{o.label}</MenuItem>)}
    </Select>
  </FormControl>
);

const MemberStatesInput: React.FC<{ value: string[]; onChange: (v: string[]) => void; label?: string }> = ({
  value, onChange, label = 'Member States potentially affected',
}) => {
  const inputSx = useInputSx();
  return (
  <Autocomplete
    multiple size="small" options={EU_MEMBER_STATES.map(([c]) => c)} value={value || []}
    onChange={(_, v) => onChange(v)}
    getOptionLabel={c => `${c} — ${MEMBER_STATE_NAMES[c] ?? c}`}
    renderTags={(v, getTagProps) => v.map((c, i) => <Chip size="small" label={c} {...getTagProps({ index: i })} key={c} />)}
    renderInput={params => <TextField {...params} label={label} sx={inputSx} />}
  />
  );
};

const TagsInput: React.FC<{ value: string[]; onChange: (v: string[]) => void; label: string; placeholder?: string }> = ({
  value, onChange, label, placeholder,
}) => {
  const inputSx = useInputSx();
  return (
  <Autocomplete
    multiple freeSolo size="small" options={[] as string[]} value={value || []}
    onChange={(_, v) => onChange(v.map(s => s.trim()).filter(Boolean))}
    renderTags={(v, getTagProps) => v.map((t, i) => <Chip size="small" label={t} {...getTagProps({ index: i })} key={t} />)}
    renderInput={params => <TextField {...params} label={label} placeholder={placeholder} sx={inputSx} />}
  />
  );
};

// ─── Assessment (incident-level) ─────────────────────────────────────────────

interface AssessmentProps {
  awarenessAt: string;
  onAwarenessChange: (iso: string) => void;
  suggestedAwareness?: string;
  entity: Nis2EntityProfile;
  onEntityChange: (patch: Partial<Nis2EntityProfile>) => void;
  significance: Nis2Significance;
  onSignificanceChange: (patch: Partial<Nis2Significance>) => void;
}

export const AssessmentForm: React.FC<AssessmentProps> = ({
  awarenessAt, onAwarenessChange, suggestedAwareness, entity, onEntityChange, significance, onSignificanceChange,
}) => {
  const theme = useTheme();
  const criteria = indicatedCriteria(significance);
  const sig = onSignificanceChange;
  const threshold = financialThreshold(significance.annual_turnover_eur);

  return (
    <>
      <SectionLabel hint="The moment the team became aware that the incident is significant — or capable of being so. Starts the 24h and 72h reporting clocks.">
        Awareness time (T0)
      </SectionLabel>
      <Grid container spacing={2} sx={{ mb: 2.5 }} alignItems="center">
        <Grid size={{ xs: 12, sm: 6 }}>
          <DateTimeInput label="Aware of significant incident at" value={awarenessAt} onChange={onAwarenessChange} />
        </Grid>
        {suggestedAwareness && !awarenessAt && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Button size="small" onClick={() => onAwarenessChange(suggestedAwareness)} sx={{ textTransform: 'none' }}>
              Use alert detection time as T0
            </Button>
          </Grid>
        )}
      </Grid>

      <SectionLabel>Notifying entity</SectionLabel>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Text label="Entity name" value={entity.entity_name} onChange={v => onEntityChange({ entity_name: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Text label="National NIS2 registration ID" value={entity.registration_id}
            onChange={v => onEntityChange({ registration_id: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SelectInput label="Entity classification" value={entity.entity_type}
            onChange={v => onEntityChange({ entity_type: v as Nis2EntityProfile['entity_type'] })}
            options={[{ value: 'essential', label: 'Essential entity' }, { value: 'important', label: 'Important entity' }]} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Text label="Sector" value={entity.sector} placeholder="e.g. Energy, Health, Digital infrastructure"
            onChange={v => onEntityChange({ sector: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <SelectInput label="Member State of establishment" value={entity.member_state}
            onChange={v => onEntityChange({ member_state: v })}
            options={EU_MEMBER_STATES.map(([c, n]) => ({ value: c, label: `${c} — ${n}` }))} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Text label="Designated CSIRT / competent authority" value={entity.authority}
            placeholder="e.g. CSIRT Italia (ACN), BSI, ANSSI, CCN-CERT"
            onChange={v => onEntityChange({ authority: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Text label="Contact name" value={entity.contact_name} onChange={v => onEntityChange({ contact_name: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Text label="Contact email" value={entity.contact_email} onChange={v => onEntityChange({ contact_email: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Text label="Contact phone" value={entity.contact_phone} onChange={v => onEntityChange({ contact_phone: v })} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CheckRow checked={entity.cir_applicable} onChange={v => onEntityChange({ cir_applicable: v })}
            label="Entity is covered by Implementing Regulation (EU) 2024/2690"
            hint="DNS, TLD, cloud, data centre, CDN, managed (security) service providers, online marketplaces, search engines, social networks, trust service providers." />
          <CheckRow checked={entity.trust_service_provider} onChange={v => onEntityChange({ trust_service_provider: v })}
            label="Trust service provider, and the incident affects its trust services"
            hint="The incident notification is then due within 24 hours instead of 72 (Art. 23(4), second subparagraph)." />
        </Grid>
      </Grid>

      <SectionLabel hint="Advisory only. SOCmate highlights criteria that appear to be met; the decision stays with you.">
        Significance assessment
      </SectionLabel>

      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>
        NIS2 Art. 23(3) — the incident has caused or is capable of causing:
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
        <CheckRow checked={significance.severe_disruption_or_loss} onChange={v => sig({ severe_disruption_or_loss: v })}
          label="Severe operational disruption of the services or financial loss for the entity" />
        <CheckRow checked={significance.considerable_damage_to_others} onChange={v => sig({ considerable_damage_to_others: v })}
          label="Considerable material or non-material damage to other natural or legal persons" />
      </Box>

      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.25 }}>
        CIR 2024/2690 general criteria (Art. 3–4)
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary, mb: 1 }}>
        {entity.cir_applicable
          ? 'Legally binding for this entity.'
          : 'Not binding for this entity — useful as guidance. Check national criteria set by your authority.'}
        {' '}Scheduled interruptions and planned maintenance are not significant incidents (Art. 3(2)).
      </Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput label="Direct financial loss (EUR, actual or potential)" value={significance.financial_loss_eur}
            onChange={v => sig({ financial_loss_eur: v })}
            helperText={`Threshold: EUR ${threshold.toLocaleString('en-GB')} (lower of €500k or 5% of turnover)`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput label="Total annual turnover, preceding financial year (EUR)" value={significance.annual_turnover_eur}
            onChange={v => sig({ annual_turnover_eur: v })} />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1.5 }}>
        <CheckRow checked={significance.trade_secret_exfiltration} onChange={v => sig({ trade_secret_exfiltration: v })}
          label="Exfiltration of trade secrets (Directive (EU) 2016/943)" />
        <CheckRow checked={significance.death} onChange={v => sig({ death: v })}
          label="Death of a natural person" />
        <CheckRow checked={significance.considerable_health_damage} onChange={v => sig({ considerable_health_damage: v })}
          label="Considerable damage to a natural person's health" />
        <CheckRow checked={significance.malicious_unauthorised_access} onChange={v => sig({ malicious_unauthorised_access: v })}
          label="Successful, suspectedly malicious and unauthorised access capable of causing severe operational disruption" />
        <CheckRow checked={significance.recurring} onChange={v => sig({ recurring: v })}
          label="Recurring incidents (≥2 in 6 months, same apparent root cause) that together exceed the financial-loss threshold" />
      </Box>

      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.25 }}>
        Entity-specific or national thresholds
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.tertiary, mb: 1 }}>
        User-count and duration thresholds differ by entity type (CIR Arts. 5–14) and by national authority,
        so they are recorded here for reference and not evaluated automatically.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput label="Affected users / recipients" value={significance.affected_users}
            onChange={v => sig({ affected_users: v })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NumberInput label="Service unavailability (hours, actual or projected)" value={significance.duration_hours}
            onChange={v => sig({ duration_hours: v })} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CheckRow checked={significance.entity_specific_threshold_met}
            onChange={v => sig({ entity_specific_threshold_met: v })}
            label="An entity-specific (CIR Arts. 5–14) or national threshold is met" />
        </Grid>
        {significance.entity_specific_threshold_met && (
          <Grid size={{ xs: 12 }}>
            <Text label="Which threshold, and why" rows={2} value={significance.entity_specific_notes}
              onChange={v => sig({ entity_specific_notes: v })} />
          </Grid>
        )}
      </Grid>

      <Banner severity={criteria.length ? 'warning' : 'info'} sx={{ mb: 2, fontSize: '0.8rem' }}>
        {criteria.length ? (
          <>
            <strong>Likely significant</strong> — {criteria.length} {criteria.length === 1 ? 'criterion' : 'criteria'} indicated:
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {criteria.map(c => <li key={c}>{c}</li>)}
            </Box>
          </>
        ) : 'No significance criteria indicated yet. Remember the clock starts when the incident is capable of meeting them.'}
      </Banner>

      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Analyst decision</Typography>
      <RadioGroup row value={significance.decision}
        onChange={e => sig({ decision: e.target.value as Nis2Significance['decision'] })} sx={{ mb: 1 }}>
        {[
          ['significant', 'Significant — mandatory reporting'],
          ['not_significant', 'Not significant'],
          ['undetermined', 'Undetermined'],
        ].map(([v, l]) => (
          <FormControlLabel key={v} value={v} control={<Radio size="small" />}
            label={<Typography sx={{ fontSize: '0.82rem' }}>{l}</Typography>} />
        ))}
      </RadioGroup>
      <Text label="Rationale" rows={2} value={significance.decision_rationale}
        onChange={v => sig({ decision_rationale: v })} />
    </>
  );
};

// ─── Stage forms ─────────────────────────────────────────────────────────────

interface StageFormProps<T> {
  value: T;
  onChange: (patch: Partial<T>) => void;
}

export const EarlyWarningForm: React.FC<StageFormProps<Nis2EarlyWarningData>> = ({ value: d, onChange }) => (
  <>
    <SectionLabel>Incident information</SectionLabel>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DateTimeInput label="Detection date/time" value={d.detection_datetime}
          onChange={v => onChange({ detection_datetime: v })} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Text label="Incident type" value={d.incident_type} onChange={v => onChange({ incident_type: v })} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Text label="Affected services / systems" value={d.affected_services}
          onChange={v => onChange({ affected_services: v })} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Text label="Initial summary" rows={3} value={d.initial_summary}
          onChange={v => onChange({ initial_summary: v })} />
      </Grid>
    </Grid>

    <SectionLabel>Early warning indicators</SectionLabel>
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
      <CheckRow checked={d.is_malicious_or_unlawful} onChange={v => onChange({ is_malicious_or_unlawful: v })}
        label="Suspected to be caused by unlawful or malicious acts" />
      <CheckRow checked={d.suspected_cross_border_impact} onChange={v => onChange({ suspected_cross_border_impact: v })}
        label="Could have a cross-border impact" />
    </Box>
    {d.suspected_cross_border_impact && (
      <Box sx={{ mb: 2 }}>
        <MemberStatesInput value={d.affected_member_states} onChange={v => onChange({ affected_member_states: v })} />
      </Box>
    )}

    <SectionLabel>Response</SectionLabel>
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
      <CheckRow checked={d.containment_initiated} onChange={v => onChange({ containment_initiated: v })}
        label="Containment initiated" />
    </Box>
    <Box sx={{ mb: 1.5 }}>
      <Text label="Measures taken" rows={3} value={d.measures_taken} onChange={v => onChange({ measures_taken: v })} />
    </Box>
    <CheckRow checked={d.assistance_requested} onChange={v => onChange({ assistance_requested: v })}
      label="Request assistance / operational guidance from the CSIRT" />
    {d.assistance_requested && (
      <Box sx={{ mt: 1 }}>
        <Text label="Assistance needed" rows={2} value={d.assistance_details}
          onChange={v => onChange({ assistance_details: v })} />
      </Box>
    )}
  </>
);

const IocEditor: React.FC<{ value: Nis2NotificationData['indicators_of_compromise']; onChange: (v: Nis2NotificationData['indicators_of_compromise']) => void }> = ({
  value, onChange,
}) => {
  const inputSx = useInputSx();
  const rows = value || [];
  const update = (i: number, patch: Partial<{ type: string; value: string }>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ ...inputSx, width: 150, flexShrink: 0 }}>
            <Select value={r.type} onChange={e => update(i, { type: e.target.value })} sx={{ fontSize: '0.8rem' }}>
              {IOC_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: '0.8rem' }}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth size="small" value={r.value} onChange={e => update(i, { value: e.target.value })}
            sx={{ ...inputSx, '& input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          <IconButton size="small" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ))}
      <Box>
        <Button size="small" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}
          onClick={() => onChange([...rows, { type: IOCType.IP, value: '' }])}>
          Add indicator
        </Button>
      </Box>
    </Box>
  );
};

export const NotificationForm: React.FC<StageFormProps<Nis2NotificationData>> = ({ value: d, onChange }) => (
  <>
    <SectionLabel>Initial assessment</SectionLabel>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <SelectInput label="Severity" value={d.severity} options={SEVERITY_OPTIONS}
          onChange={v => onChange({ severity: v as Nis2NotificationData['severity'] })} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <SelectInput label="Threat category" value={d.threat_category} options={THREAT_CATEGORY_OPTIONS}
          onChange={v => onChange({ threat_category: v as Nis2NotificationData['threat_category'] })} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Text label="Assessment of severity and impact" rows={3} value={d.initial_assessment}
          onChange={v => onChange({ initial_assessment: v })} />
      </Grid>
    </Grid>

    <SectionLabel hint="Estimates are fine at this stage — refine them in the final report.">Impact estimates</SectionLabel>
    <Grid container spacing={2} sx={{ mb: 1 }}>
      <Grid size={{ xs: 12, sm: 4 }}>
        <NumberInput label="Affected users" value={d.affected_users_count}
          onChange={v => onChange({ affected_users_count: v })} />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <NumberInput label="Downtime (minutes)" value={d.downtime_minutes}
          onChange={v => onChange({ downtime_minutes: v })} />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <NumberInput label="Est. financial loss (EUR)" value={d.estimated_financial_loss_eur}
          onChange={v => onChange({ estimated_financial_loss_eur: v })} />
      </Grid>
    </Grid>
    <Box sx={{ mb: 2 }}>
      <CheckRow checked={d.gdpr_breach_triggered} onChange={v => onChange({ gdpr_breach_triggered: v })}
        label="Personal data breach — GDPR Art. 33 notification to the supervisory authority also required" />
    </Box>

    <SectionLabel hint="Art. 23(4)(b) requires updating the early warning information.">Update of early warning</SectionLabel>
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
      <CheckRow checked={d.is_malicious_or_unlawful} onChange={v => onChange({ is_malicious_or_unlawful: v })}
        label="Suspected to be caused by unlawful or malicious acts" />
      <CheckRow checked={d.suspected_cross_border_impact} onChange={v => onChange({ suspected_cross_border_impact: v })}
        label="Could have a cross-border impact" />
    </Box>
    {d.suspected_cross_border_impact && (
      <Box sx={{ mb: 2 }}>
        <MemberStatesInput value={d.affected_member_states} onChange={v => onChange({ affected_member_states: v })} />
      </Box>
    )}
    <Box sx={{ mb: 2 }}>
      <Text label="Measures taken" rows={3} value={d.measures_taken} onChange={v => onChange({ measures_taken: v })} />
    </Box>

    <SectionLabel hint="Pre-filled from the ticket's IoCs. Remove anything that must not be shared.">
      Indicators of compromise
    </SectionLabel>
    <IocEditor value={d.indicators_of_compromise} onChange={v => onChange({ indicators_of_compromise: v })} />
  </>
);

export const IntermediateForm: React.FC<StageFormProps<Nis2IntermediateData>> = ({ value: d, onChange }) => (
  <>
    <SectionLabel>Authority request</SectionLabel>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DateTimeInput label="Requested on" value={d.requested_at} onChange={v => onChange({ requested_at: v })} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Text label="Request reference" value={d.request_reference}
          onChange={v => onChange({ request_reference: v })} />
      </Grid>
    </Grid>
    <SectionLabel>Status update</SectionLabel>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <SelectInput label="Current status" value={d.current_status}
          onChange={v => onChange({ current_status: v as Nis2IntermediateData['current_status'] })}
          options={[
            { value: 'ongoing', label: 'Ongoing' },
            { value: 'contained', label: 'Contained' },
            { value: 'resolved', label: 'Resolved' },
          ]} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Text label="Relevant status updates" rows={4} value={d.status_update}
          onChange={v => onChange({ status_update: v })} />
      </Grid>
    </Grid>
  </>
);

export const FinalForm: React.FC<StageFormProps<Nis2FinalData>> = ({ value: d, onChange }) => (
  <>
    <SectionLabel>Report type</SectionLabel>
    <RadioGroup row value={d.is_incident_resolved ? 'final' : 'progress'}
      onChange={e => onChange({ is_incident_resolved: e.target.value === 'final' })} sx={{ mb: 1 }}>
      <FormControlLabel value="final" control={<Radio size="small" />}
        label={<Typography sx={{ fontSize: '0.82rem' }}>Final report — incident handled</Typography>} />
      <FormControlLabel value="progress" control={<Radio size="small" />}
        label={<Typography sx={{ fontSize: '0.82rem' }}>Progress report — incident still ongoing</Typography>} />
    </RadioGroup>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DateTimeInput label="Incident handling concluded at" value={d.handling_concluded_at}
          onChange={v => onChange({ handling_concluded_at: v })}
          helperText={d.is_incident_resolved ? undefined : 'Set once handled — the final report is due one month later.'} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <SelectInput label="Severity" value={d.severity} options={SEVERITY_OPTIONS}
          onChange={v => onChange({ severity: v as Nis2FinalData['severity'] })} />
      </Grid>
    </Grid>

    <SectionLabel>Incident description</SectionLabel>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12 }}>
        <Text label="Detailed description" rows={4} value={d.detailed_description}
          onChange={v => onChange({ detailed_description: v })} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Text label="Impact (services, users, financial, cross-sector)" rows={2} value={d.impact_summary}
          onChange={v => onChange({ impact_summary: v })} />
      </Grid>
    </Grid>

    <SectionLabel>Threat and root cause</SectionLabel>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12 }}>
        <Text label="Type of threat or root cause" rows={3} value={d.root_cause_analysis}
          onChange={v => onChange({ root_cause_analysis: v })} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TagsInput label="MITRE ATT&CK techniques" placeholder="e.g. T1190 — press Enter"
          value={d.mitre_attack_techniques} onChange={v => onChange({ mitre_attack_techniques: v })} />
      </Grid>
    </Grid>

    <SectionLabel>Mitigation</SectionLabel>
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Text label="Applied mitigation measures" rows={3} value={d.mitigations_implemented}
          onChange={v => onChange({ mitigations_implemented: v })} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Text label="Ongoing mitigation measures" rows={3} value={d.mitigations_ongoing}
          onChange={v => onChange({ mitigations_ongoing: v })} />
      </Grid>
    </Grid>

    <SectionLabel>Cross-border impact and lessons learned</SectionLabel>
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Text label="Cross-border impact" rows={2} value={d.cross_border_impact}
          onChange={v => onChange({ cross_border_impact: v })} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Text label="Lessons learned" rows={2} value={d.lessons_learned}
          onChange={v => onChange({ lessons_learned: v })} />
      </Grid>
    </Grid>
  </>
);
